import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';

import appLogger from 'app/shared/loggers/appLogger';

// Utils
import { getMonthDropdown } from 'app/shared/utils/dateUtils';

import {
    Button,
    ButtonGroup,
    Flex,
    Select,
    Spacer,
    Text,
    TextButton,
} from '@zillow/constellation';

import HeadWrapper from 'app/shared/components/HeadWrapper';
import AllSpendTable from 'app/shared/components/AllSpendReport/AllSpendTable';
import BoostAdTable from 'app/shared/components/BoostAdReport/BoostAdTable';

// Actions
import AnalyticsActions from 'app/shared/actions/AnalyticsActions';
import ModalActions from 'app/shared/actions/ModalActions';

// containers
import AuthCheckWrapper from 'app/shared/containers/AuthCheckWrapper';

import { RMWContext } from 'app/shared/providers/RMWContext';

import analyticsUtils from 'app/client/utils/analyticsUtils';
import adproductsUtils from 'app/shared/utils/adproductsUtils';
import queryUtils from 'app/shared/utils/queryUtils';
import adSpendUtils from 'app/shared/utils/adSpendUtils';
import AnalyticsConstants from 'app/shared/constants/AnalyticsConstants';
import ModalConstants from 'app/shared/constants/ModalConstants';
import QueryConstants from 'app/shared/constants/QueryConstants';
import sharedConfig from 'app/shared/sharedConfig';
import stringHelper from 'app/shared/utils/stringHelper';

// Selectors
import { AppSelectors, AnalyticsSelectors } from 'app/shared/selectors';

const { startCaseRemoveSpace } = stringHelper;
const DEFAULT_PAGE_TITLE = 'Ad Spend';
const MONTH_DROPDOWN = getMonthDropdown();
const ANALYTICS_CATEGORIES = {
    ALL: 'All Spend Page',
    BOOST: 'Boost Spend Page',
};

const TABS = {
    ALL: 'ALL',
    BOOST: 'BOOST',
};

const propTypes = {
    coreProductInvoice: PropTypes.object.isRequired,
    boostAdReport: PropTypes.object.isRequired,
    location: PropTypes.object.isRequired,
};

class AdSpendPage extends React.Component {
    static contextType = RMWContext;

    constructor(props) {
        super(props);

        const queryParams = queryUtils.parse(props.location.search);
        const insertionOrderId = queryParams[QueryConstants.AD];

        this.state = {
            isLoading: true,
            insertionOrderId,
            activeTab: insertionOrderId !== undefined ? TABS.BOOST : TABS.ALL,
            startDate: MONTH_DROPDOWN[0].value,
            hasFetchedDataOnce: false,
        };
        if (insertionOrderId !== undefined) {
            this.props.dispatch(analyticsUtils.track.GAEventV2(AnalyticsConstants.GA_EVENTS.LOAD_BOOST_AD));
        }

        this.handlePropertyClick = this.handlePropertyClick.bind(this);
        this.handleAdClick = this.handleAdClick.bind(this);
        this.handleDownloadClick = this.handleDownloadClick.bind(this);
        this.handleMonthChange = this.handleMonthChange.bind(this);
        this.handleAllSpendClick = this.handleAllSpendClick.bind(this);
        this.handleBoostClick = this.handleBoostClick.bind(this);
        this.getData = this.getData.bind(this);
    }

    componentDidMount() {
        const { insertionOrderId, activeTab } = this.state;
        if (insertionOrderId) {
            this.props.dispatch(ModalActions.showModal({
                modalType: ModalConstants.BOOST_AD_PREVIEWS,
                insertionOrderId,
            }));
        }
        this.getData(activeTab);
    }

    handleAdClick(insertionOrderId) {
        this.props.dispatch(AnalyticsActions.getBoostAdPreviews({ insertionOrderId }))
            .then((response) => {
                const header = response.propertyDetails.name;

                this.setState({ insertionOrderId });
                this.props.dispatch(ModalActions.showModal({
                    modalType: ModalConstants.BOOST_AD_PREVIEWS,
                    header,
                    boostAdPreviews: response,
                }));
                this.props.dispatch(analyticsUtils.track.GAEventV2(AnalyticsConstants.GA_EVENTS.VIEW_BOOST_AD));
            })
            .catch((error) => {
                this.props.dispatch(ModalActions.showModal({
                    modalType: ModalConstants.ERROR_MODAL,
                    header: 'Error',
                    error,
                }));
            });
    }

    handlePropertyClick(listingKey) {
        const { dispatch } = this.props;

        dispatch(
            analyticsUtils.track.GAEventV2(AnalyticsConstants.GA_EVENTS.PROPERTY_COLUMN_CLICK, {
                category: startCaseRemoveSpace(ANALYTICS_CATEGORIES[this.state.activeTab]),
            })
        );

        const { paths } = this.context;
        const url = `${paths.propertiesPage}/${listingKey}/${paths.propertyDetailsListing}`;
        window.router.transitionTo(url);
    }

    handleDownloadClick() {
        const { dispatch } = this.props;

        dispatch(
            analyticsUtils.track.GAEventV2(AnalyticsConstants.GA_EVENTS.DOWNLOAD_DATA_CLICK, {
                category: startCaseRemoveSpace(ANALYTICS_CATEGORIES[this.state.activeTab]),
            })
        );

        const { startDate } = this.state;
        const { paths } = this.context;
        const startDateParam = startDate ? `?startDateYyyyMmDd=${startDate}` : '';
        const excelPath = this.state.activeTab === TABS.ALL ?
            paths.exportExcel.adSpendAll :
            paths.exportExcel.boostAd;
        const excelUrl = `${paths.proxy}/${sharedConfig.baseApi}${excelPath}${startDateParam}`;
        window.open(excelUrl);
    }

    handleMonthChange(e) {
        e.preventDefault();
        const startDate = e.target.value;
        this.getData(this.state.activeTab, startDate);
    }

    handleAllSpendClick() {
        if (this.state.activeTab === TABS.ALL) {
            return;
        }
        this.props.dispatch(
            analyticsUtils.track.GAEventV2(AnalyticsConstants.GA_EVENTS.AD_SPEND_SWITCHER_CLICK, {
                category: startCaseRemoveSpace(ANALYTICS_CATEGORIES[TABS.ALL]),
            })
        );
        this.setState({ activeTab: TABS.ALL });
        this.getData(TABS.ALL);
    }

    handleBoostClick() {
        if (this.state.activeTab === TABS.BOOST) {
            return;
        }
        this.props.dispatch(
            analyticsUtils.track.GAEventV2(AnalyticsConstants.GA_EVENTS.AD_SPEND_SWITCHER_CLICK, {
                category: startCaseRemoveSpace(ANALYTICS_CATEGORIES[TABS.BOOST]),
            })
        );
        this.setState({ activeTab: TABS.BOOST });
        this.getData(TABS.BOOST);
    }

    getData(dataTab, startDate = this.state.startDate) {
        const params = { startDateYyyyMmDd: startDate };
        const fetchData = dataTab === TABS.ALL ?
            AnalyticsActions.getCoreProductInvoice :
            AnalyticsActions.getBoostAdReport;

        this.setState({
            isLoading: true,
            startDate,
        });

        this.props.dispatch(fetchData(params))
            .then((result) => {
                const data = result.payload;

                if (data.success === false) {
                    this.setState({
                        isLoading: false,
                        error: 'Sorry, something went wrong. Please try again later.',
                    });
                } else {
                    this.setState({
                        isLoading: false,
                        hasFetchedDataOnce: true,
                    });
                }
            })
            .catch((errorRMWModel) => {
                appLogger.logError({
                    message: errorRMWModel.message,
                    context: 'AdSpendPage.getData',
                });
            });
    }

    renderTotal() {
        const {
            coreProductInvoice, boostAdReport, isScreenMobile, isScreenXS,
        } = this.props;
        const { isLoading, activeTab } = this.state;

        if (isLoading) {
            return null;
        }
        const totals = activeTab === TABS.ALL ?
            adSpendUtils.getCoreProductInvoiceTotals(coreProductInvoice, isScreenMobile) :
            adSpendUtils.getBoostAdTotals(boostAdReport, isScreenMobile);

        return (
            <Spacer paddingY="xs">
                <Flex display="flex" justifyContent="space-between" alignItems="center">
                    {isScreenXS && this.renderLink()}
                    <Text fontType="body">{totals.join(' | ')}</Text>
                </Flex>
            </Spacer>
        );
    }

    renderTable() {
        const { isLoading, error } = this.state;
        const {
            coreProductInvoice,
            boostAdReport,
            isScreenMobile,
        } = this.props;
        if (this.state.activeTab === TABS.ALL) {
            return (
                <AllSpendTable
                    coreProductInvoiceModel={coreProductInvoice}
                    isScreenMobile={isScreenMobile}
                    onPropertyClick={this.handlePropertyClick}
                    isLoading={isLoading}
                    errorMessage={error}
                />
            );
        }
        return (
            <BoostAdTable
                boostAdReport={boostAdReport}
                isScreenMobile={isScreenMobile}
                onPropertyClick={this.handlePropertyClick}
                onAdClick={this.handleAdClick}
                isLoading={isLoading}
                errorMessage={error}
            />
        );
    }

    renderLink() {
        const { isLoading } = this.state;
        if (isLoading) {
            return null;
        }

        return (
            <Spacer paddingY="xs">
                <TextButton onClick={this.handleDownloadClick}>
                    Export details
                </TextButton>
            </Spacer>
        );
    }

    renderSwitcher() {
        const { activeTab } = this.state;
        return (
            <Spacer paddingY="xs">
                <ButtonGroup conjoined={true} fluid={true} aria-label="Spend options">
                    <Button
                        onClick={this.handleAllSpendClick}
                        buttonType={activeTab === TABS.ALL ? 'primary' : 'secondary'}
                    >
                        All spend
                    </Button>
                    <Button
                        onClick={this.handleBoostClick}
                        buttonType={activeTab === TABS.BOOST ? 'primary' : 'secondary'}
                    >
                        Boost
                    </Button>
                </ButtonGroup>
            </Spacer>
        );
    }

    renderDropDown() {
        const { startDate, isLoading } = this.state;
        return (
            <Spacer paddingY="xs">
                <Select
                    name="startDate"
                    onChange={this.handleMonthChange}
                    disabled={isLoading}
                    value={startDate}
                >
                    {MONTH_DROPDOWN.map(({ value, name }) => (
                        <option key={value} value={value}>
                            {name}
                        </option>
                    ))}
                </Select>
            </Spacer>
        );
    }

    renderTopPage() {
        const { coreProductInvoice } = this.props;
        const onlyPackageProducts = coreProductInvoice
            && adproductsUtils.containsOnlyPackageProducts(coreProductInvoice.listingInvoices);
        const showSwitcher = coreProductInvoice && coreProductInvoice.pastBoostAvailable && !onlyPackageProducts;
        return (
            <Spacer paddingY="xs">
                {showSwitcher ? this.renderSwitcherTopPage() : this.renderNoSwitcherTopPage()}
            </Spacer>
        );
    }

    renderNoSwitcherTopPage() {
        const { isScreenXS } = this.props;

        return (
            <>
                <Flex display="flex" justifyContent="space-between" alignItems="center">
                    {this.renderDropDown()}
                    {!isScreenXS && this.renderLink()}
                </Flex>
                {this.renderTotal()}
            </>
        );
    }

    renderSwitcherTopPage() {
        const { isScreenXS } = this.props;

        if (isScreenXS) {
            return (
                <>
                    {this.renderSwitcher()}
                    {this.renderDropDown()}
                    {this.renderTotal()}
                </>
            );
        }

        return (
            <>

                <Flex display="flex" justifyContent="space-between" alignItems="center">
                    {this.renderSwitcher()}
                    {this.renderLink()}
                </Flex>
                <Flex display="flex" justifyContent="space-between" alignItems="center">
                    {this.renderDropDown()}
                    {this.renderTotal()}
                </Flex>
            </>
        );
    }

    render() {
        const { hasFetchedDataOnce } = this.state;
        return (
            <AuthCheckWrapper requireStrongAuthentication={false}>
                <div className="AdSpendPage">
                    <HeadWrapper title={DEFAULT_PAGE_TITLE} />
                    {hasFetchedDataOnce && this.renderTopPage()}
                    {this.renderTable()}
                </div>
            </AuthCheckWrapper>
        );
    }
}

const mapStateToProps = state => ({
    coreProductInvoice: AnalyticsSelectors.makeCoreProductInvoice(state),
    boostAdReport: AnalyticsSelectors.makeBoostAdReport(state),
    isScreenMobile: AppSelectors.makeIsScreenMobile(state),
    isScreenXS: AppSelectors.makeIsScreenXS(state),
});

AdSpendPage.propTypes = propTypes;

export { AdSpendPage };
export default connect(mapStateToProps)(AdSpendPage);
